import { computed, ref, watch } from 'vue'
import { useNow } from '@/composables/game/time'
import type { PathwayEvent } from '@/types/shared/pathway-event'
import { useConfig } from '@/composables/game/config'
import { usePlayer } from '@/composables/game/player'
import { backButtonCallback } from '@/router'
import type { Nullable } from '@/types/shared/common'
import type { SPECIAL_CURRENCY_ID } from '@/types/shared/player'
import { useLocalStorage } from '@vueuse/core'
import { buildDropeeStorageKey } from '@/config/config'
import { ECOMMERCE_ITEM_BENEFIT_TYPE, ECOMMERCE_ITEM_TAG } from '@/types/shared/player-config'
import { isTodayDate } from '@/helpers/date'
import { DateTime } from 'luxon'

export type FrontendPathwayEvent = PathwayEvent & { hidden: boolean }

export const usePathwayEvents = () => {
  const { player } = usePlayer()
  const { config } = useConfig()
  const { now } = useNow()

  const pathwayEvents = computed((): FrontendPathwayEvent[] =>
    config.value.game.pathwayEvents.filter(event => {
      const progress = player.value.pathwayEvents.runningEvents[event.id]
      if (!progress) {
        return false
      }
      return event.startDate <= now.value && event.endDate >= now.value
    }).map(event => {
      const progress = player.value.pathwayEvents.runningEvents[event.id]

      return {
        ...event,
        hidden: progress.currentStep >= 13,
      }
    },
    ),
  )

  const anyActivePathwayEvent = computed(() => pathwayEvents.value.filter(event => !event.hidden).length > 0)

  return {
    pathwayEvents,
    anyActivePathwayEvent,
  }
}

export const usePathwayEvent = (pathwayEvent: PathwayEvent) => {
  const { now } = useNow()
  const { syncConfig } = useConfig()
  const { player, updatePlayer } = usePlayer()

  const maybePlayerProgress = computed(() => player.value.pathwayEvents.runningEvents[pathwayEvent.id])
  const playerProgress = computed(() => {
    if (!maybePlayerProgress.value) throw new Error('Player pathway event progress not found for the id ' + pathwayEvent.id)
    return maybePlayerProgress.value
  })

  const countdownForEventEnd = computed(() => {
    const eventEndTime = playerProgress.value.startTimestamp + playerProgress.value.duration
    return Math.max(eventEndTime - now.value, 0)
  })

  const hasFinishedCurrentProgress = computed(() => playerProgress.value.currentStep >= Object.keys(pathwayEvent.config.steps).length)

  watch(countdownForEventEnd, async countdownForEventEnd => {
    if (countdownForEventEnd === 0) {
      setTimeout(async () => {
        const { playerStats } = await syncConfig()
        updatePlayer(playerStats)
      }, 1000)
    }
  })

  return {
    countdownForEventEnd,
    playerProgress,
    hasFinishedCurrentProgress,
  }
}

const selectedPathwayEvent = ref<PathwayEvent | null>(null)
export const usePathwayEventOverlay = () => {

  const showPathwayEventOverlay = computed(() => !!selectedPathwayEvent.value)

  function hidePathwayEventOverlay() {
    backButtonCallback.value = null
    selectedPathwayEvent.value = null
  }

  function setSelectedPathwayEvent(event: PathwayEvent) {
    backButtonCallback.value = hidePathwayEventOverlay
    selectedPathwayEvent.value = event
  }

  function showFirstPathway(pathwayEvents: FrontendPathwayEvent[]) {
    setSelectedPathwayEvent(pathwayEvents.find(event => !event.hidden) as PathwayEvent)
  }

  return {
    showPathwayEventOverlay,
    hidePathwayEventOverlay,
    setSelectedPathwayEvent,
    selectedPathwayEvent,
    showFirstPathway,
  }
}

export const useOutOfSpecialCurrency = () => {
  const { config } = useConfig()
  const { nowDateIso } = useNow()
  const selectedSpecialCurrencyForModal = ref<Nullable<SPECIAL_CURRENCY_ID>>(null)
  const modalCloseDate = useLocalStorage<Partial<Record<SPECIAL_CURRENCY_ID, string>>>(buildDropeeStorageKey('pathwayOutOfCurrencyModalCloseDate'), {})

  function getAvailableEcommerceItems(currencyId: SPECIAL_CURRENCY_ID) {
    return config.value.game.ecommerce.items.filter(item => item.tags.includes(ECOMMERCE_ITEM_TAG.OUT_OF_SPECIAL_CURRENCY) && item.benefits.some(b => b.type === ECOMMERCE_ITEM_BENEFIT_TYPE.SPECIAL_CURRENCY && b.currency === currencyId) && !item.archivedAt)
  }

  function canShowOutOfSpecialCurrencyModal(currencyId: SPECIAL_CURRENCY_ID) {
    const ecommerceItems = getAvailableEcommerceItems(currencyId)
    if (!ecommerceItems.length) return false
    if (modalCloseDate.value[currencyId] !== undefined) {
      return !isTodayDate(DateTime.fromISO(modalCloseDate.value[currencyId]))
    }
    return true
  }

  function closeOutOfSpecialCurrencyModal(hideForToday: boolean) {
    if (hideForToday && selectedSpecialCurrencyForModal.value) {
      modalCloseDate.value[selectedSpecialCurrencyForModal.value] = nowDateIso.value
    }
    selectedSpecialCurrencyForModal.value = null
  }

  return {
    canShowOutOfSpecialCurrencyModal,
    selectedSpecialCurrencyForModal,
    closeOutOfSpecialCurrencyModal,
    getAvailableEcommerceItems,
  }
}